import { Matrix4 } from "three";
import {
  exportToDraco,
  exportToDracoWithTransform,
  exportToDracoInWorldSpace,
  ExportTarget,
  setExportTarget,
  convertMatrixForExportTarget,
  convertVector3ForExportTarget,
} from "../../helpers/drcExporter.js";
import { globalData } from "../../helpers/globData.ts";
// import { iprOverlays } from "../../helpers/ipr";
import { numberingUniversal } from "../../helpers/teethNumbering.js";
import ApolloExporter from "../customizer/Apollo/ApolloExporter.ts";
import ETPExporter from "../customizer/Choho/ETPExporter.ts";
import { getToothNumber } from "../hierarchyHelper";
import { lowerMatrix, upperMatrix } from "./Bitejump.ts";
import { Jaw } from "./interfaces.ts";
import { FETreatmentGeneration } from "./Mesh";
import { teethMovementData, teethMovementDataPerStep } from "./Scene";
import { attachmentArray1 } from "./teethControls/addRemoveAttachment";

const iprOverlays = globalData.iprOverlays;

function unnToFdi(unn) {
  return String(numberingUniversal[unn]?.fdi);
}

function getToothFdi(tooth) {
  return unnToFdi(getToothNumber(tooth));
}

export async function exportToApollo(exportTarget = ExportTarget.Default) {
  setExportTarget(exportTarget);
  globalData.centeringOffset =
    FETreatmentGeneration.getCurrentStep().parent.matrixWorld.clone();

  const teeth = await prepareTeethExport();
  const gum = await prepareGumExport();
  const attachments = await prepareAttachmentsExport();
  const attachmentIDMapper = {};

  const apolloExporter = new ApolloExporter(4);

  teeth.forEach(tooth => {
    apolloExporter.addTooth(tooth.fdi, tooth.crownData);
  });
  apolloExporter.setMaxillaryGingiva(gum.upper);
  apolloExporter.setMandibularGingiva(gum.lower);
  console.log(apolloExporter);

  attachments.forEach((attachment, index) => {
    if (attachment?.data) {
      apolloExporter.addAttachment(attachment.name, index, attachment.data);
      attachmentIDMapper[attachment.name] = index;
    }
  });

  apolloExporter.addTreatmentStep(
    await exportStep(
      FETreatmentGeneration.steps[FETreatmentGeneration.currentStep],
      attachmentIDMapper,
    ),
  );

  apolloExporter.download();
}
export async function exportToETP(exportTarget = ExportTarget.Default) {
  setExportTarget(exportTarget);
  FETreatmentGeneration.moveToStep(0);
  globalData.centeringOffset =
    FETreatmentGeneration.getCurrentStep().parent.matrixWorld.clone();

  const teeth = await prepareTeethExport();
  const teethAnatomy = await prepareTeethAnatomyExport();
  const simulatedRoots = globalData.isRoot
    ? await prepareSimulatedRootsExport()
    : null;
  console.log(simulatedRoots);

  const ponticTeeth = await preparePonticTeethExport();
  const gums = await prepareGumExport();
  const attachments = await prepareAttachmentsExport();
  const attachmentIDMapper = {};

  const bones = await prepareBonesExport();

  const etpExporter = new ETPExporter();

  teeth.forEach(tooth => {
    etpExporter.addTooth(
      tooth.fdi,
      tooth.crownData,
      teethAnatomy[tooth.fdi],
      simulatedRoots ? simulatedRoots[tooth.fdi] : undefined,
    );
  });

  ponticTeeth.forEach(pontic => {
    etpExporter.addPontic(pontic.id, pontic.crownData);
  });
  if (gums.upper) {
    etpExporter.setMaxillaryGingiva(gums.upper);
  }
  if (gums.lower) {
    etpExporter.setMandibularGingiva(gums.lower);
  }

  if (bones.upper) {
    etpExporter.setMaxillaryBone(bones.upper);
  }
  if (bones.lower) {
    etpExporter.setMandibularBone(bones.lower);
  }

  attachments.forEach((attachment, index) => {
    if (attachment?.data) {
      etpExporter.addAttachment(attachment.name, index, attachment.data);
      attachmentIDMapper[attachment.name] = index;
    }
  });

  if (globalData.boltonAnalysisInfo) {
    const toothSizeDiscrepancies = [];

    if (globalData.boltonAnalysisInfo.toothWidthTable) {
      Object.entries(globalData.boltonAnalysisInfo.toothWidthTable).forEach(
        ([unn, data]) => {
          if (data !== null) {
            toothSizeDiscrepancies.push({
              fdi: unnToFdi(parseInt(unn)),
              sizeDifference: data,
            });
          }
        },
      );
    }

    etpExporter.setBoltonAnalysis({
      overAllRatio: globalData.boltonAnalysisInfo.overAllRatio,
      anteriorRatio: globalData.boltonAnalysisInfo.anteriorRatio,
      lowerArchLength: globalData.boltonAnalysisInfo.lowerArchLength,
      upperArchLength: globalData.boltonAnalysisInfo.upperArchLength,
      toothSizeDiscrepancies,
    });
  }

  ponticsIndex = 0;
  for (
    let stepIndex = 0;
    stepIndex < FETreatmentGeneration.steps.length - 1;
    stepIndex++
  ) {
    FETreatmentGeneration.moveToStep(stepIndex);
    etpExporter.addTreatmentStep(
      await exportStep(
        FETreatmentGeneration.steps[stepIndex],
        attachmentIDMapper,
      ),
    );
  }
  console.log(etpExporter.toJSON());

  etpExporter.download();
}
async function exportStep(step, attachmentIDMapper) {
  const treatmentStep = {
    step: getStepNumber(step),
    teeth: getTeethStepInfo(step, attachmentIDMapper),
  };

  const pontics = getPonticTeethStepInfo(step);
  if (Object.keys(pontics).length > 0) {
    treatmentStep.pontics = pontics;
  }

  const jawInfo = []; /* getJawInfo(step); */
  if (Object.keys(jawInfo).length > 0) {
    treatmentStep.jawInfo = jawInfo;
  }

  const overBite = getOverBiteInfo(step);
  if (overBite !== null) {
    treatmentStep.overBite = overBite;
  }

  const overJet = getOverJetInfo(step);
  if (overJet !== null) {
    treatmentStep.overJet = overJet;
  }
  const attachments = [];
  if (overJet !== null) {
    treatmentStep.attachments = attachments;
  }
  return treatmentStep;
}
let ponticsIndex = 0;
function getPonticTeethStepInfo(step) {
  let ponticsInfo = {};

  const teeth = step.children
    .flatMap(gum => gum.children)
    .filter(tooth => tooth.userData["isPontics"]);
  teeth.forEach((pontic, index) => {
    pontic.updateWorldMatrix(true, false);
    ponticsInfo[getToothFdi(pontic)] = {
      id: index + ponticsIndex++,
      transformationMatrix: convertMatrixForExportTarget(
        new Matrix4()
          .copy(globalData.centeringOffset)
          .invert()
          .multiply(pontic.matrixWorld),
      ),
    };
  });
  return ponticsInfo;
}

function getTeethStepInfo(step, attachmentIDMapper) {
  let teethInfo = {};

  const teeth = step.children
    .flatMap(gum => gum.children)
    .filter(tooth => !tooth.userData["isPontics"]);
  teeth.forEach(tooth => {
    teethInfo[getToothFdi(tooth)] = {
      // toothState: "existing",
      // transformationMatrix: tooth.matrixWorld
      //   .clone()
      //   // .multiply(globalData.centeringOffset)
      //   .toArray(),
      attachments: getToothAttachmentStepInfo(tooth, attachmentIDMapper), // attachments on this tooth in this step (including Button Cutouts)
      // ipr: getToothIPRInfo(tooth), // IPR needed for this tooth in this step
      // movement: getTeethMovementDataForTooth(tooth), // cumulative dental movement values for this tooth in this step
    };
  });
  return teethInfo;
}
function getTeethMovementDataForTooth(tooth) {
  return teethMovementDataPerStep[tooth.userData["step"]][
    tooth.userData["number"]
  ];
}
function getToothIPRInfo(tooth) {
  const toothNumber = getToothNumber(tooth);
  const stepNumber = tooth.userData["step"];
  const isLower = tooth.userData["jaw"] === Jaw.Lower;

  let mesialIPR = 0;
  let distalIPR = 0;

  // Get IPR data for current step
  const IPRData = iprOverlays[isLower ? 1 : 0];
  const toothIPRArray = iprOverlays[isLower ? 1 : 0]["t" + toothNumber];

  if (toothIPRArray !== undefined) {
    // Find the IPR entry for the current step
    const currentStepIPR = toothIPRArray.find(ipr => ipr?.step === stepNumber);

    if (isLower) {
      // For lower jaw
      if (toothNumber >= 25) {
        mesialIPR = currentStepIPR ? currentStepIPR.amount / 2 : 0;

        for (let offset = 1; offset <= 8; offset++) {
          const adjacentToothNum = toothNumber + offset;
          if (adjacentToothNum > 32) break;

          const adjacentIPRArray = IPRData["t" + adjacentToothNum];
          const adjacentStepIPR = adjacentIPRArray?.find(
            ipr => ipr?.step === stepNumber,
          );

          if (adjacentStepIPR) {
            distalIPR = adjacentStepIPR.amount / 2;
            break;
          }
        }
      } else {
        distalIPR = currentStepIPR ? currentStepIPR.amount / 2 : 0;

        for (let offset = 1; offset <= 8; offset++) {
          const adjacentToothNum = toothNumber - offset;
          if (adjacentToothNum < 17) break;

          const adjacentIPRArray = IPRData["t" + adjacentToothNum];
          const adjacentStepIPR = adjacentIPRArray?.find(
            ipr => ipr?.step === stepNumber,
          );

          if (adjacentStepIPR) {
            mesialIPR = adjacentStepIPR.amount / 2;
            break;
          }
        }
      }
    } else {
      // For upper jaw
      if (toothNumber <= 8) {
        mesialIPR = currentStepIPR ? currentStepIPR.amount / 2 : 0;

        for (let offset = 1; offset <= 8; offset++) {
          const adjacentToothNum = toothNumber - offset;
          if (adjacentToothNum < 1) break;

          const adjacentIPRArray = IPRData["t" + adjacentToothNum];
          const adjacentStepIPR = adjacentIPRArray?.find(
            ipr => ipr?.step === stepNumber,
          );

          if (adjacentStepIPR) {
            distalIPR = adjacentStepIPR.amount / 2;
            break;
          }
        }
      } else {
        distalIPR = currentStepIPR ? currentStepIPR.amount / 2 : 0;

        for (let offset = 1; offset <= 8; offset++) {
          const adjacentToothNum = toothNumber + offset;
          if (adjacentToothNum > 16) break;

          const adjacentIPRArray = IPRData["t" + adjacentToothNum];
          const adjacentStepIPR = adjacentIPRArray?.find(
            ipr => ipr?.step === stepNumber,
          );

          if (adjacentStepIPR) {
            mesialIPR = adjacentStepIPR.amount / 2;
            break;
          }
        }
      }
    }
  }

  return { mesialIPR, distalIPR };
}

function getToothAttachmentStepInfo(tooth, attachmentIDMapper) {
  const attachments = tooth.children[0].children;
  const result = [];
  attachments.forEach(attachment => {
    attachment.updateWorldMatrix(true, false);
    result.push({
      id: attachmentIDMapper[attachment.name],
      transformationMatrix: convertMatrixForExportTarget(
        new Matrix4()
          .copy(globalData.centeringOffset)
          .invert()
          .multiply(attachment.matrixWorld),
      ),
    });
  });
  return result;
}

function getOverBiteInfo(step) {
  const result = {};
  const stepNumber = getStepNumber(step);
  if (stepNumber != 0 && stepNumber != globalData.maxStepNumber) {
    return null;
  }
  const stepType = stepNumber === 0 ? "initial" : "final";
  const data = globalData.OBOJ?.overbite;
  const keys = Object.keys(data || {});
  for (const key of keys) {
    result[key] = data[key]?.[stepType] ?? null;
  }
  return result;
}

function getOverJetInfo(step) {
  const result = {};
  const stepNumber = getStepNumber(step);
  if (stepNumber != 0 && stepNumber != globalData.maxStepNumber) {
    return null;
  }
  const stepType = stepNumber === 0 ? "initial" : "final";

  const data = globalData.OBOJ?.overjet;
  console.log(data);
  const keys = Object.keys(data || {});
  for (const key of keys) {
    result[key] = data[key]?.[stepType] ?? null;
  }
  return result;
}
function getJawInfo(step) {
  const jawInfo = {};
  const stepNumber = getStepNumber(step);
  step.children.forEach(jaw => {
    const jawType =
      jaw.userData["jaw"] === Jaw.Lower ? "mandibular" : "maxillary";
    const isLower = jaw.userData["jaw"] === Jaw.Lower;

    let isPassive = false;
    let isOverCorrection = false;
    if (globalData.is.isPassiveAligner) {
      const stepDiff = Math.abs(globalData.lowerSteps - globalData.upperSteps);
      const hasMoreSteps = isLower
        ? globalData.upperSteps > globalData.lowerSteps
        : globalData.lowerSteps > globalData.upperSteps;

      if (globalData.is.isStartTogether) {
        isPassive = stepNumber < stepDiff && hasMoreSteps;
      } else {
        const maxSteps = isLower
          ? globalData.lowerSteps
          : globalData.upperSteps;
        isPassive = stepNumber > maxSteps;
      }
    }

    const upperOverCorrection = globalData.upperOverCorrectionSteps || 0;
    const lowerOverCorrection = globalData.lowerOverCorrectionSteps || 0;

    if (isLower) {
      if (lowerOverCorrection > 0) {
        const maxSteps = globalData.lowerSteps;
        isOverCorrection = stepNumber >= maxSteps - lowerOverCorrection;
      }
    } else {
      if (upperOverCorrection > 0) {
        const maxSteps = globalData.upperSteps;
        isOverCorrection = stepNumber >= maxSteps - upperOverCorrection;
      }
    }

    jawInfo[jawType] = {};

    if (isPassive) {
      jawInfo[jawType].isPassive = true;
    }

    if (isOverCorrection) {
      jawInfo[jawType].isOverCorrection = true;
    }

    if (stepNumber === globalData.maxStepNumber) {
      jawInfo[jawType].BJMatrix = convertMatrixForExportTarget(
        isLower ? lowerMatrix : upperMatrix,
      );
    }
  });

  return jawInfo;
}
function getStepNumber(step) {
  return step.userData["step"];
}
async function prepareTeethExport() {
  const teethPromises = FETreatmentGeneration.teeth
    .filter(tooth => !tooth.userData["isPontics"])
    .map(async tooth => ({
      fdi: getToothFdi(tooth),
      crownData: await exportToDracoInWorldSpace(
        tooth,
        globalData.centeringOffset,
      ),
    }));

  const teeth = await Promise.all(teethPromises);
  return teeth;
}
async function prepareTeethAnatomyExport() {
  const teethAnatomyPromises = FETreatmentGeneration.teeth
    .filter(tooth => !tooth.userData["isPontics"])
    .map(async tooth => {
      const toothNumber = getToothNumber(tooth);
      const toothData = teethMovementData[toothNumber];

      return {
        fdi: unnToFdi(toothNumber),
        data: {
          centerOfRotation:
            convertVector3ForExportTarget(
              vector3ToArray(toothData.ToothRotationCenter),
            ) || null,
          axes: {
            longAxis: convertVector3ForExportTarget(
              vector3ToArray(toothData.ToothOrientation.Up),
            ),
            facialAxis: convertVector3ForExportTarget(
              vector3ToArray(toothData.ToothOrientation.Front),
            ),
            mesialAxis: convertVector3ForExportTarget(
              vector3ToArray(toothData.ToothOrientation.Side),
            ),
          },
        },
      };
    });

  const apolloTeeth = await Promise.all(teethAnatomyPromises);

  return apolloTeeth.reduce((acc, { fdi, data }) => {
    acc[fdi] = { data };
    return acc;
  }, {});
}
async function prepareSimulatedRootsExport() {
  const rootSimulationPromises = FETreatmentGeneration.teeth
    .filter(tooth => !tooth.userData["isPontics"])
    .map(async tooth => {
      const fdi = getToothFdi(tooth);
      const rootChild = tooth.children[3].children[0];
      const data = rootChild
        ? await exportToDracoInWorldSpace(rootChild, globalData.centeringOffset)
        : null;

      return {
        fdi,
        data,
      };
    });

  const rootSimulationTeeth = await Promise.all(rootSimulationPromises);

  return rootSimulationTeeth.reduce((acc, { fdi, data }) => {
    acc[fdi] = data;
    return acc;
  }, {});
}
async function preparePonticTeethExport() {
  const treatmentStepsPromises = FETreatmentGeneration.steps
    .flatMap(step => step.children.flatMap(gum => gum.children))
    .filter(tooth => tooth.userData["isPontics"])
    .map(async (ponticTooth, index) => ({
      step: ponticTooth.userData["step"],
      id: index,
      fdi: getToothFdi(ponticTooth),
      crownData: await exportToDracoInWorldSpace(
        ponticTooth,
        globalData.centeringOffset,
      ),
    }));

  const teeth = await Promise.all(treatmentStepsPromises);
  return teeth;
}
async function prepareGumExport() {
  const currentStep = FETreatmentGeneration.getCurrentStep();
  const gums = {
    upper: await exportToDracoInWorldSpace(
      currentStep.children[0],
      globalData.centeringOffset,
    ),
    lower: await exportToDracoInWorldSpace(
      currentStep.children[1],
      globalData.centeringOffset,
    ),
  };

  return gums;
}
async function prepareAttachmentsExport() {
  const attachments = [];
  const seen = new Set();

  const flatAttachments = attachmentArray1.flat();

  await Promise.all(
    flatAttachments.map(async attachment => {
      if (!seen.has(attachment.name)) {
        seen.add(attachment.name);
        // Export raw local geometry — the per-step transformationMatrix (matrixWorld)
        // in getToothAttachmentStepInfo will position it in world space
        attachments.push({
          name: attachment.name,
          data: await exportToDracoWithTransform(attachment),
        });
      }
    }),
  );

  return attachments;
}
async function prepareBonesExport() {
  const currentStepBones = FETreatmentGeneration.bones;

  if (!currentStepBones) {
    return { upper: null, lower: null };
  }

  const [upper, lower] = await Promise.all([
    currentStepBones.skull
      ? exportToDracoInWorldSpace(
          currentStepBones.skull,
          globalData.centeringOffset,
        )
      : null,
    currentStepBones.mandible
      ? exportToDracoInWorldSpace(
          currentStepBones.mandible,
          globalData.centeringOffset,
        )
      : null,
  ]);

  return { upper, lower };
}

function vector3ToArray(vector) {
  return [vector.x, vector.y, vector.z];
}
